import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../../context/ThemeContext";
import { useCompanyForm } from "../../../Layouts/admin/components/sections/companies/hooks";
import {
  CompanyList,
  CompanyPagination,
  CompanyDetailsModal,
  CompanyAddModal,
  CompanyFilters,
  CompanySourcesModal,
  CompanyRetransmissionsModal,
} from "../../../Layouts/admin/components/sections/companies/components";
import type { ThemeClasses, Company } from "../../../Layouts/admin/components/sections/companies/types";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import { useMultiSelect, BulkActionBar, type BulkAction } from "../../../Layouts/reseller-admin/shared";
import Tooltip from "../../../components/ui/Tooltip";
import api from "../../../utils/api";
import { escapeHtml } from "../../../utils/escapeHtml";

export default function CompaniesSection() {
  const { t } = useTranslation(["adminSections", "companies"]);
  const { isDarkMode } = useDarkMode();

  // Cleanup scroll lock on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const {
    // Data
    companies: companiesRaw,
    meta,
    loading,
    selectedCompany,

    // Modal states
    isModalOpen,
    showAddModal,
    loadingSubmit,
    imagePreview,
    isEdit,

    // Form
    form,

    // Filter states
    selectedStatus,
    searchQuery,
    currentPage,

    // Handlers
    handleStatusChange,
    handleSearchChange,
    handleDetailsClick,
    handleEditClick,
    handleAddClick,
    handleImageChange,
    handleSubmitCompany,
    setCurrentPage,
    closeModal,
    closeAddModal,
    handleSuspendCompany,
    handleActivateCompany,
    handleDeleteCompany,

    // Action loading state
    loadingAction,
    actionLabel,
    actionIcon,
  } = useCompanyForm();

  // El store tipa Company con campos opcionales; los componentes de admin usan
  // un Company más estricto. Alineamos el tipo para los hijos y helpers.
  const companies = companiesRaw as unknown as Company[];

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const totalPages = meta?.totalPages || 1;

  const [selectedView, setSelectedView] = useState<"grid" | "list">("grid");
  const [sourcesCompany, setSourcesCompany] = useState<Company | null>(null);
  const [retransmissionsCompany, setRetransmissionsCompany] = useState<Company | null>(null);
  const [showSgModal, setShowSgModal] = useState(false);
  const [selectedSgCompanies, setSelectedSgCompanies] = useState<Set<string>>(new Set());
  const [modalStep, setModalStep] = useState<"companies" | "devices">("companies");
  const [currentCompanyIndex, setCurrentCompanyIndex] = useState(0);
  const [updateType, setUpdateType] = useState<"manual" | "cascade" | null>(null);
  const [selectedDevices, setSelectedDevices] = useState<Set<string>>(new Set());
  const [companiesPage, setCompaniesPage] = useState(1);
  const companiesPerPage = 5;


  const [sgCompanies, setSgCompanies] = useState<Array<{ id: string; name: string; currentStatus: string; sgStatus: string }>>([]);
  const [sgLoading, setSgLoading] = useState(false);
  const [sgDevicesData, setSgDevicesData] = useState<Record<string, Array<{ id: string; name: string; imei: string; currentStatus: string; sgStatus: string; newStatus?: string; vehicle_id?: string }>>>({});
  const [sgVehiclesData, setSgVehiclesData] = useState<Record<string, Array<{ id: string; name: string; plate: string }>>>({});
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [deviceStatusChanges, setDeviceStatusChanges] = useState<Record<string, string>>({});
  const [isDeviceSelectionMode, setIsDeviceSelectionMode] = useState(false);

  // Fetch SG companies when modal opens (only if not already loaded)
  useEffect(() => {
    if (showSgModal && sgCompanies.length === 0) {
      fetchSgCompanies();
    }
  }, [showSgModal, sgCompanies.length]);

  const fetchSgCompanies = async () => {
    setSgLoading(true);
    try {
      const response = await api.get("/companies/compare-sg");
      const data = response.data.data;

      // Map backend data to frontend format
      const mappedCompanies = data.map((company: any) => ({
        id: company.id,
        name: company.name,
        currentStatus: company.v5_status,
        sgStatus: company.sg_status,
      }));

      setSgCompanies(mappedCompanies);
    } catch (error) {
      console.error("Error fetching SG companies:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar las compañías de SG",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setSgLoading(false);
    }
  };

  const fetchDevicesForCompanies = async (companyIds: string[]) => {
    setDevicesLoading(true);
    try {
      const devicesData: Record<string, Array<{ id: string; name: string; imei: string; currentStatus: string; sgStatus: string; vehicle_id?: string }>> = {};
      const vehiclesData: Record<string, Array<{ id: string; name: string; plate: string }>> = {};

      for (const companyId of companyIds) {
        try {
          // Fetch devices
          const devicesResponse = await api.get(`/devices/by-company?company_id=${companyId}`);
          const devices = devicesResponse.data.data || devicesResponse.data;

          // Map backend device data to frontend format
          const mappedDevices = devices.map((device: any) => ({
            id: device.id,
            name: device.name || device.imei,
            imei: device.imei,
            currentStatus: device.status_id === "R5tW0uX4yY9cF6dH2aI8" ? "activo" : "inactivo",
            currentStatusId: device.status_id,
            sgStatus: "desconocido", // Will be updated later when SG API is integrated
            vehicle_id: device.vehicle_id,
          }));

          devicesData[companyId] = mappedDevices;

          // Fetch vehicles
          const vehiclesResponse = await api.get(`/vehicles/by-company?company_id=${companyId}`);
          const vehicles = vehiclesResponse.data.data || vehiclesResponse.data || [];

          // Map backend vehicle data to frontend format
          const mappedVehicles = vehicles.map((vehicle: any) => ({
            id: vehicle.id,
            name: vehicle.name,
            plate: vehicle.plate,
          }));

          vehiclesData[companyId] = mappedVehicles;
        } catch (error) {
          console.error(`Error fetching data for company ${companyId}:`, error);
          devicesData[companyId] = [];
          vehiclesData[companyId] = [];
        }
      }

      setSgDevicesData(devicesData);
      setSgVehiclesData(vehiclesData);

      // Return the data to avoid race condition
      return { devicesData, vehiclesData };
    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los datos",
        confirmButtonColor: brand.primary,
      });
      return { devicesData: {}, vehiclesData: {} };
    } finally {
      setDevicesLoading(false);
    }
  };

  // Pagination for companies
  const sgTotalPages = Math.ceil(sgCompanies.length / companiesPerPage);
  const sgStartIndex = (companiesPage - 1) * companiesPerPage;
  const sgEndIndex = sgStartIndex + companiesPerPage;
  const paginatedCompanies = sgCompanies.slice(sgStartIndex, sgEndIndex);

  const selectedCompaniesList = sgCompanies.filter(c => selectedSgCompanies.has(c.id));
  const currentCompany = selectedCompaniesList[currentCompanyIndex];

  // Pagination for selected companies (manual mode)
  const selectedCompaniesTotalPages = Math.ceil(selectedCompaniesList.length / companiesPerPage);

  // Check if any selected company has facturable status in SG
  const hasFacturableSelected = selectedCompaniesList.some(c => c.sgStatus === 'facturable');

  // Multi-select functionality
  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    toggleSelectionMode,
    selectedCount,
    getSelectedItems,
  } = useMultiSelect<Company>();

  const handleToggleSelection = useCallback((company: Company) => {
    toggleSelection(company.id);
  }, [toggleSelection]);

  const [bulkLoading, setBulkLoading] = useState(false);
  const updateCompany = useCompaniesStore((state) => state.updateCompany);
  const fetchCompanies = useCompaniesStore((state) => state.fetchCompanies);

  const handleBulkSuspend = useCallback(async () => {
    const selectedCompanies = getSelectedItems(companies);
    if (selectedCompanies.length === 0) return;

    const result = await Swal.fire({
      title: t("companies.bulkActionsModals.suspend.title"),
      text: t("companies.bulkActionsModals.suspend.confirmText", { count: selectedCompanies.length }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: t("companies.bulkActionsModals.suspend.confirmButton"),
      cancelButtonText: t("companies.bulkActionsModals.suspend.cancelButton"),
    });

    if (!result.isConfirmed) return;

    setBulkLoading(true);
    try {
      for (const company of selectedCompanies) {
        await updateCompany(company.id, {
          status_id: "Y2aD7bE1fF6jM3kO9hP5",
        });
      }
      clearSelection();
      await Swal.fire({
        title: t("companies.bulkActionsModals.suspend.successTitle"),
        text: t("companies.bulkActionsModals.suspend.successText", { count: selectedCompanies.length }),
        icon: "success",
        confirmButtonText: t("companies.bulkActionsModals.suspend.acceptButton"),
        confirmButtonColor: brand.primary,
      });
      fetchCompanies();
    } catch (error) {
      console.error("Error al suspender compañías:", error);
      await Swal.fire({
        title: t("companies.bulkActionsModals.suspend.errorTitle"),
        text: t("companies.bulkActionsModals.suspend.errorText"),
        icon: "error",
        confirmButtonText: t("companies.bulkActionsModals.suspend.acceptButton"),
        confirmButtonColor: brand.primary,
      });
    } finally {
      setBulkLoading(false);
    }
  }, [companies, getSelectedItems, updateCompany, clearSelection, fetchCompanies, brand.primary, t]);

  const handleBulkActivate = useCallback(async () => {
    const selectedCompanies = getSelectedItems(companies);
    if (selectedCompanies.length === 0) return;

    const result = await Swal.fire({
      title: t("companies.bulkActionsModals.activate.title"),
      text: t("companies.bulkActionsModals.activate.confirmText", { count: selectedCompanies.length }),
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: brand.primary,
      cancelButtonColor: "#6b7280",
      confirmButtonText: t("companies.bulkActionsModals.activate.confirmButton"),
      cancelButtonText: t("companies.bulkActionsModals.activate.cancelButton"),
    });

    if (!result.isConfirmed) return;

    setBulkLoading(true);
    try {
      for (const company of selectedCompanies) {
        await updateCompany(company.id, {
          status_id: "jXmjzHcBD5E1J0wjwHzq",
        });
      }
      clearSelection();
      await Swal.fire({
        title: t("companies.bulkActionsModals.activate.successTitle"),
        text: t("companies.bulkActionsModals.activate.successText", { count: selectedCompanies.length }),
        icon: "success",
        confirmButtonText: t("companies.bulkActionsModals.activate.acceptButton"),
        confirmButtonColor: brand.primary,
      });
      fetchCompanies();
    } catch (error) {
      console.error("Error al activar compañías:", error);
      await Swal.fire({
        title: t("companies.bulkActionsModals.activate.errorTitle"),
        text: t("companies.bulkActionsModals.activate.errorText"),
        icon: "error",
        confirmButtonText: t("companies.bulkActionsModals.activate.acceptButton"),
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setBulkLoading(false);
    }
  }, [getSelectedItems, companies, updateCompany, fetchCompanies, clearSelection, t]);

  const bulkActions: BulkAction[] = [
    {
      id: "activate",
      label: t("companies.bulkActions.activate"),
      icon: "ri-check-line",
      onClick: handleBulkActivate,
      variant: "success",
    },
    {
      id: "suspend",
      label: t("companies.bulkActions.suspend"),
      icon: "ri-close-circle-line",
      onClick: handleBulkSuspend,
      variant: "danger",
    },
  ];

  
  return (
    <div className="h-full flex flex-col">
      {/* Loading Overlay for Single Actions */}
      {loadingAction && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div
            className={`flex flex-col items-center gap-5 rounded-3xl px-12 py-10 shadow-2xl border ${themeClasses.bg} ${themeClasses.border}`}
          >
            <div className="relative h-16 w-16">
              <div className="absolute inset-0 rounded-full border-4 border-primary-500/20"></div>
              <div className="absolute inset-0 animate-spin rounded-full border-4 border-transparent border-t-primary-500"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <i className={`${actionIcon} text-2xl text-primary-500`}></i>
              </div>
            </div>
            <div className="text-center">
              <p className={`text-base font-semibold ${themeClasses.text}`}>
                {actionLabel || t("companies.processing")}
              </p>
              <p className={`mt-1 text-xs ${themeClasses.textSecondary}`}>
                Por favor espera un momento…
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className={`${themeClasses.bg} ${themeClasses.border} p-4 sm:p-6 pb-0`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4">
          <div className="min-w-0">
            <h1 className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}>
              {t("companies.title")}
            </h1>
            <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
              {t("companies.subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-2">

            {/* {!isMonitor && !disableCRUD && ( */}
              <button
                onClick={handleAddClick}
                className="bg-primary-500 hover:bg-primary-400 text-gray-900 px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors whitespace-nowrap text-sm sm:text-base self-start sm:self-auto flex-shrink-0"
              >
                <i className="ri-add-line"></i>
                <span className="hidden sm:inline">{t("companies.addCompany")}</span>
                <span className="sm:hidden">Nuevo</span>
              </button>
            {/* )} */}
          </div>
          </div>
      </div>

      {/* Filters */}
      <div className={`${themeClasses.bg} border-b ${themeClasses.border} px-6 pb-6`}>
        <div className="flex items-center justify-between flex-col md:flex-row md:gap-4 gap-2">
          <div className="flex items-center flex-col md:flex-row md:gap-4 gap-2 w-full md:w-auto">
            <CompanyFilters
              selectedStatus={selectedStatus}
              searchQuery={searchQuery}
              isDarkMode={isDarkMode}
              themeClasses={themeClasses}
              onStatusChange={handleStatusChange}
              onSearchChange={handleSearchChange}
              t={t}
            />
          </div>
          {/* View Toggle and Selection Mode */}
          <div className="flex items-center gap-2">
            {/* Selection Mode Toggle */}
            <button
              onClick={toggleSelectionMode}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                isSelectionMode
                  ? "bg-primary-500 text-gray-900"
                  : `${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText}`
              }`}
            >
              <i className={`${isSelectionMode ? "ri-checkbox-multiple-fill" : "ri-checkbox-multiple-line"}`}></i>
              <span className="hidden sm:inline">
                {isSelectionMode ? t("companies.exitSelection") : t("companies.selectMultiple")}
              </span>
            </button>

            {/* Actualizar con SG Button */}
            {/* <button
              onClick={() => setShowSgModal(true)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText}`}
            >
              <i className="ri-refresh-line"></i>
              <span className="hidden sm:inline">{t("companies.updateWithSG")}</span>
            </button> */}

            {/* Select All Button (only in selection mode) */}
            {isSelectionMode && (
              <button
                onClick={() => selectAll(companies as Company[])}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText}`}
              >
                <i className="ri-checkbox-fill mr-1"></i>
                <span className="hidden sm:inline">{t("companies.selectAll")}</span>
              </button>
            )}

            {/* View Toggle */}
            <div
              className={`flex ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg p-1`}
            >
              <button
                className={`px-3 py-1 rounded-md transition-colors ${selectedView === "grid"
                  ? ""
                  : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
                  }`}
                style={selectedView === "grid" ? { backgroundColor: brand.primary, color: brand.accent } : undefined}
                onClick={() => setSelectedView("grid")}
              >
                <i className="ri-grid-fill"></i>
              </button>
              <button
                className={`px-3 py-1 rounded-md transition-colors ${selectedView === "list"
                  ? ""
                  : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
                  }`}
                style={selectedView === "list" ? { backgroundColor: brand.primary, color: brand.accent } : undefined}
                onClick={() => setSelectedView("list")}
              >
                <i className="ri-list-check"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: brand.primary }}></div>
          </div>
        ) : (
          <>
            <CompanyList
              companies={companies}
              themeClasses={themeClasses}
              isDarkMode={isDarkMode}
              selectedView={selectedView}
              onDetails={handleDetailsClick}
              onEdit={handleEditClick}
              onDelete={handleSuspendCompany}
              onActivate={handleActivateCompany}
              onSoftDelete={handleDeleteCompany}
              onSources={(company) => setSourcesCompany(company)}
              onRetransmissions={(company) => setRetransmissionsCompany(company)}
              t={t}
              isSelectionMode={isSelectionMode}
              selectedIds={selectedIds}
              onToggleSelection={handleToggleSelection}
            />

            {companies.length > 0 && (
              <CompanyPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                t={t}
              />
            )}
          </>
        )}
      </div>

      {/* Company Add/Edit Modal */}
      {showAddModal && (
        <CompanyAddModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          form={form}
          loadingSubmit={loadingSubmit}
          onClose={closeAddModal}
          onSubmit={handleSubmitCompany}
          onImageChange={handleImageChange}
          imagePreview={imagePreview}
          isEdit={isEdit}
        />
      )}

      {/* Company Details Modal */}
      {isModalOpen && selectedCompany && (
        <CompanyDetailsModal
          company={selectedCompany}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onClose={closeModal}
          onEdit={async (company) => {
            await handleEditClick(company);
            closeModal();
          }}
          onDelete={handleSuspendCompany}
          onSoftDelete={handleDeleteCompany}
          t={t}
        />
      )}

      {/* Company Sources Modal */}
      {sourcesCompany && (
        <CompanySourcesModal
          company={sourcesCompany}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onClose={() => setSourcesCompany(null)}
        />
      )}

      {/* Company Retransmissions Modal */}
      {retransmissionsCompany && (
        <CompanyRetransmissionsModal
          company={retransmissionsCompany}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onClose={() => setRetransmissionsCompany(null)}
        />
      )}

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedCount={selectedCount}
        actions={bulkActions}
        onClearSelection={clearSelection}
        isDarkMode={isDarkMode}
      />

      {/* Bulk Loading Overlay */}
      {bulkLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themeClasses.cardBg} rounded-lg p-6 flex flex-col items-center space-y-4`}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: brand.primary }}></div>
            <p className={`${themeClasses.text} font-medium`}>
              {t("companies.processingBulkAction")}
            </p>
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              {t("companies.pleaseWait")}
            </p>
          </div>
        </div>
      )}

      {/* SG Update Modal */}
      {showSgModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${themeClasses.cardBg} rounded-xl shadow-2xl p-6 w-full max-w-4xl mx-4 max-h-[95vh] flex flex-col`}>

            <div className={`flex justify-between items-center mb-6 pb-4 border-b ${isDarkMode ? 'border-gray-700' : ''}`}>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center">
                  <i className="ri-refresh-line text-xl text-primary-500"></i>
                </div>
                <div>
                  <h2 className={`text-xl font-bold ${themeClasses.text}`}>{t("companies.sgModal.title")}</h2>
                  <p className={`text-sm ${themeClasses.textSecondary}`}>
                    {modalStep === "companies" ? t("companies.sgModal.description") : `${t("companies.sgModal.step2.devicesOf")} ${currentCompany?.name}`}
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  setShowSgModal(false);
                  setModalStep("companies");
                  setSelectedSgCompanies(new Set());
                  setCurrentCompanyIndex(0);
                  setCompaniesPage(1);
                  setSgCompanies([]);
                }}
                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${themeClasses.buttonHover} ${themeClasses.buttonText}`}
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className={`flex items-center gap-2 ${modalStep === "companies" ? "text-primary-500" : themeClasses.textSecondary}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${modalStep === "companies" ? (isDarkMode ? "bg-primary-600 text-white" : "bg-primary-500 text-gray-900") : (isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-700")}`}>
                  1
                </div>
                <span className="text-sm font-medium">Compañías</span>
              </div>
              <div className={`w-12 h-0.5 ${modalStep === "devices" ? "bg-primary-500" : (isDarkMode ? "bg-gray-600" : "bg-gray-300")}`}></div>
              <div className={`flex items-center gap-2 ${modalStep === "devices" ? "text-primary-500" : themeClasses.textSecondary}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${modalStep === "devices" ? (isDarkMode ? "bg-primary-600 text-white" : "bg-primary-500 text-gray-900") : (isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-700")}`}>
                  2
                </div>
                <span className="text-sm font-medium">Dispositivos</span>
              </div>
            </div>

            {/* Content */}
            <div className="h-[500px] overflow-hidden relative">

              {/* Loading Overlay */}
              {sgLoading && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: brand.primary }}></div>
                    <p className={`${themeClasses.text} font-medium`}>Cargando compañías...</p>
                  </div>
                </div>
              )}

              {/* Step 1: Companies */}
              <div className={`h-full transition-transform duration-300 ease-in-out ${modalStep === "companies" ? "translate-x-0" : "-translate-x-full absolute inset-0"}`}>
                <div className="flex flex-col justify-between h-full overflow-y-auto rounded-lg">
                  <table className="w-full max-h-[200px]">
                    <thead className={`sticky top-0 ${themeClasses.cardBg} bg-gradient-to-r from-transparent via-transparent to-transparent`}>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider w-12">
                          <input
                            type="checkbox"
                            checked={selectedSgCompanies.size === sgCompanies.filter(c => c.sgStatus === 'facturable').length && sgCompanies.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedSgCompanies(new Set(sgCompanies.filter(c => c.sgStatus === 'facturable').map(c => c.id)));
                              } else {
                                setSelectedSgCompanies(new Set());
                              }
                            }}
                            className="rounded border-gray-300 dark:border-dark-border text-primary-500 focus:ring-primary-500"
                          />
                        </th>
                        <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${themeClasses.textSecondary}`}>
                          Nombre de la compañía
                        </th>
                        <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${themeClasses.textSecondary}`}>
                          Status actual
                        </th>
                        <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${themeClasses.textSecondary}`}>
                          Status SG
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCompanies.map((company, index) => (
                        <tr
                          key={company.id}
                          onClick={() => {
                            if (company.sgStatus === 'terminado' || company.sgStatus === 'desconocido') return;
                            const newSelected = new Set(selectedSgCompanies);
                            if (newSelected.has(company.id)) {
                              newSelected.delete(company.id);
                            } else {
                              newSelected.add(company.id);
                            }
                            setSelectedSgCompanies(newSelected);
                          }}
                          className={`hover:bg-primary-500/5 dark:hover:bg-primary-500/10 transition-colors ${company.sgStatus === 'terminado' || company.sgStatus === 'desconocido' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${themeClasses.text} ${selectedSgCompanies.has(company.id) ? 'bg-primary-500/10 dark:bg-primary-500/20' : ''}`}
                        >
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedSgCompanies.has(company.id)}
                              disabled={company.sgStatus === 'terminado' || company.sgStatus === 'desconocido'}
                              onChange={(e) => {
                                const newSelected = new Set(selectedSgCompanies);
                                if (e.target.checked) {
                                  newSelected.add(company.id);
                                } else {
                                  newSelected.delete(company.id);
                                }
                                setSelectedSgCompanies(newSelected);
                              }}
                              className="rounded border-gray-300 dark:border-gray-600 text-primary-500 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500/20 to-primary-500/10 flex items-center justify-center text-xs font-bold text-primary-500">
                                {index + 1}
                              </div>
                              <span className="text-sm font-medium">{company.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              company.currentStatus === 'facturable'
                                ? 'bg-emerald-500 text-white dark:bg-emerald-600 border border-emerald-600'
                                : 'bg-rose-500 text-white dark:bg-rose-600 border border-rose-600'
                            }`}>
                              <i className={`mr-1.5 ${company.currentStatus === 'facturable' ? 'ri-check-line' : 'ri-close-line'}`}></i>
                              {company.currentStatus === 'facturable' ? t("companies.sgModal.step1.status.facturable") : t("companies.sgModal.step1.status.notFacturable")}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              company.sgStatus === 'facturable'
                                ? 'bg-blue-500 text-white dark:bg-blue-600 border border-blue-600'
                                : company.sgStatus === 'terminado'
                                ? 'bg-gray-500 text-white dark:bg-gray-600 border border-gray-600'
                                : 'bg-amber-500 text-white dark:bg-amber-600 border border-amber-600'
                            }`}>
                              <i className={`ri-database-2-line mr-1.5`}></i>
                              {company.sgStatus === 'facturable' ? t("companies.sgModal.step1.status.facturable") : company.sgStatus === 'terminado' ? t("companies.sgModal.step1.status.notFacturable") : t("companies.sgModal.step1.status.unknown")}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {sgTotalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => setCompaniesPage(Math.max(1, companiesPage - 1))}
                        disabled={companiesPage === 1}
                        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText} disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <i className="ri-arrow-left-s-line"></i>
                      </button>
                      <span className={`text-sm ${themeClasses.text}`}>
                        {t("companies.sgModal.step1.company")} {companiesPage} {t("companies.sgModal.step1.of")} {sgTotalPages}
                      </span>
                      <button
                        onClick={() => setCompaniesPage(Math.min(sgTotalPages, companiesPage + 1))}
                        disabled={companiesPage === sgTotalPages}
                        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText} disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Step 2: Devices */}
              <div className={`h-full transition-transform duration-300 ease-in-out ${modalStep === "devices" ? "translate-x-0" : "translate-x-full absolute inset-0"}`}>
                <div className="h-full overflow-y-auto rounded-lg">
                  {currentCompany && (
                    <>
                      <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                          <h3 className={`text-lg font-semibold ${themeClasses.text}`}>
                            {currentCompany.name}
                            {updateType === "cascade" && (
                              <span className={`ml-2 text-xs px-2 py-1 rounded-full bg-blue-500 text-white dark:bg-blue-600 border border-blue-600`}>
                                {t("companies.sgModal.modes.cascade")}
                              </span>
                            )}
                            {updateType === "manual" && (
                              <span className={`ml-2 text-xs px-2 py-1 rounded-full bg-primary-500 text-white dark:bg-primary-600 border border-primary-600`}>
                                {t("companies.sgModal.modes.manual")}
                              </span>
                            )}
                          </h3>
                          {/* Pagination for companies */}
                          {selectedCompaniesTotalPages > 1 && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setCurrentCompanyIndex(Math.max(0, currentCompanyIndex - 1))}
                                disabled={currentCompanyIndex === 0}
                                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText} disabled:opacity-50 disabled:cursor-not-allowed`}
                              >
                                <i className="ri-arrow-left-s-line"></i>
                              </button>
                              <span className={`text-xs ${themeClasses.text}`}>
                                {t("companies.sgModal.step1.company")} {currentCompanyIndex + 1} {t("companies.sgModal.step1.of")} {selectedCompaniesList.length}
                              </span>
                              <button
                                onClick={() => setCurrentCompanyIndex(Math.min(selectedCompaniesList.length - 1, currentCompanyIndex + 1))}
                                disabled={currentCompanyIndex === selectedCompaniesList.length - 1}
                                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText} disabled:opacity-50 disabled:cursor-not-allowed`}
                              >
                                <i className="ri-arrow-right-s-line"></i>
                              </button>
                            </div>
                          )}
                        </div>
                        <div className={`text-sm ${themeClasses.textSecondary}`}>
                          {currentCompanyIndex + 1} {t("companies.sgModal.step2.of")} {selectedCompaniesList.length}
                        </div>
                      </div>
                      <table className="w-full">
                        <thead className={`sticky top-0 ${themeClasses.cardBg} bg-gradient-to-r from-transparent via-transparent to-transparent`}>
                          <tr className="border-b border-gray-200 dark:border-gray-700">
                            {updateType === "manual" && isDeviceSelectionMode && (
                              <th className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  checked={sgDevicesData[currentCompany.id]?.every(d => selectedDevices.has(d.id))}
                                  onChange={(e) => {
                                    const devices = sgDevicesData[currentCompany.id] || [];
                                    if (e.target.checked) {
                                      setSelectedDevices(prev => new Set([...prev, ...devices.map(d => d.id)]));
                                    } else {
                                      setSelectedDevices(prev => {
                                        const newSet = new Set(prev);
                                        devices.forEach(d => newSet.delete(d.id));
                                        return newSet;
                                      });
                                    }
                                  }}
                                  className="rounded border-gray-300 dark:border-gray-600"
                                />
                              </th>
                            )}
                            <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${themeClasses.textSecondary}`}>
                              {t("companies.sgModal.step2.imei")}
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${themeClasses.textSecondary}`}>
                              Status actual
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${themeClasses.textSecondary}`}>
                              Nuevo status
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sgDevicesData[currentCompany.id]?.map((device, index) => (
                            <tr key={device.id} className={`hover:bg-primary-500/5 dark:hover:bg-primary-500/10 transition-colors cursor-pointer ${themeClasses.text} ${updateType === "manual" && selectedDevices.has(device.id) ? 'bg-primary-500/10 dark:bg-primary-500/20' : ''}`}>
                              {updateType === "manual" && isDeviceSelectionMode && (
                                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={selectedDevices.has(device.id)}
                                    onChange={(e) => {
                                      const newSelected = new Set(selectedDevices);
                                      if (e.target.checked) {
                                        newSelected.add(device.id);
                                      } else {
                                        newSelected.delete(device.id);
                                      }
                                      setSelectedDevices(newSelected);
                                    }}
                                    className="rounded border-gray-300 dark:border-gray-600 text-primary-500 focus:ring-primary-500"
                                  />
                                </td>
                              )}
                              <td className="px-4 py-3" onClick={() => {
                                if (updateType === "manual" && isDeviceSelectionMode) {
                                  const newSelected = new Set(selectedDevices);
                                  if (newSelected.has(device.id)) {
                                    newSelected.delete(device.id);
                                  } else {
                                    newSelected.add(device.id);
                                  }
                                  setSelectedDevices(newSelected);
                                }
                              }}>
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500/20 to-primary-500/10 flex items-center justify-center text-xs font-bold text-primary-500">
                                    {index + 1}
                                  </div>
                                  <span className="text-sm font-medium">{device.name}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm font-mono">{device.imei}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                  device.currentStatus === 'activo'
                                    ? 'bg-emerald-500 text-white dark:bg-emerald-600 border border-emerald-600'
                                    : 'bg-rose-500 text-white dark:bg-rose-600 border border-rose-600'
                                }`}>
                                  {device.currentStatus === 'activo' ? 'Activo' : 'Inactivo'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <select
                                  value={deviceStatusChanges[device.id] || ''}
                                  onChange={(e) => {
                                    setDeviceStatusChanges(prev => ({
                                      ...prev,
                                      [device.id]: e.target.value
                                    }));
                                  }}
                                  className={`px-3 py-1 rounded-lg text-xs font-medium border ${
                                    deviceStatusChanges[device.id] === 'activo'
                                      ? 'bg-emerald-500 text-white border-emerald-600'
                                      : deviceStatusChanges[device.id] === 'inactivo'
                                      ? 'bg-rose-500 text-white border-rose-600'
                                      : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
                                  }`}
                                >
                                  <option value="">-</option>
                                  <option value="activo">Activo</option>
                                  <option value="inactivo">Inactivo</option>
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pt-4 gap-3 w-full">
              <div className={`text-sm ${themeClasses.textSecondary}`}>
                {modalStep === "companies" && selectedSgCompanies.size > 0 && (
                  <span className="flex items-center gap-2">
                    <i className="ri-checkbox-multiple-fill text-primary-500"></i>
                    {selectedSgCompanies.size} {t("companies.sgModal.step1.of")} {sgCompanies.length} {t("companies.sgModal.step1.selected")}
                  </span>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                {modalStep === "companies" ? (
                  <>
                    <button
                      onClick={() => setSelectedSgCompanies(new Set())}
                      disabled={selectedSgCompanies.size === 0}
                      className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText} disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md`}
                    >
                      <i className="ri-delete-bin-line mr-1 sm:mr-2"></i>
                      {t("companies.sgModal.buttons.clear")}
                    </button>
                    <Tooltip content={t("companies.sgModal.tooltips.manual")}>
                      <button
                        onClick={async () => {
                          if (selectedSgCompanies.size > 0) {
                            setUpdateType("manual");
                            await fetchDevicesForCompanies(Array.from(selectedSgCompanies));
                            setModalStep("devices");
                            setCurrentCompanyIndex(0);
                            setSelectedDevices(new Set());
                          }
                        }}
                        disabled={selectedSgCompanies.size === 0 || !hasFacturableSelected || devicesLoading}
                        className="bg-primary-500 hover:bg-primary-400 text-gray-900 px-3 sm:px-5 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 sm:gap-2 w-full"
                      >
                        <i className="ri-edit-line"></i>
                        <span>{devicesLoading ? "Cargando..." : t("companies.sgModal.buttons.manual")}</span>
                      </button>
                    </Tooltip>
                    <div className="relative">
                      <Tooltip content={t("companies.sgModal.tooltips.cascade")}>
                        <button
                          onClick={async () => {
                            // Get all companies for cascade update (not just facturable)
                            const allCompanyIds = sgCompanies.map(c => c.id);


                            // Fetch vehicles for all companies before showing cascade modal
                            const { devicesData, vehiclesData } = await fetchDevicesForCompanies(allCompanyIds);

                            // Ocultar el modal de fondo antes de mostrar el modal de cascada
                            setShowSgModal(false);

                            // Mostrar modal de confirmación con detalles de las compañías y sus entidades
                            const companiesDetails = sgCompanies.map(c => ({
                                name: c.name,
                                sgStatus: c.sgStatus,
                                deviceCount: devicesData[c.id]?.length || 0,
                                vehicleCount: vehiclesData[c.id]?.length || 0
                              }));


                              const result = await Swal.fire({
                                title: t("companies.sgModal.cascadeModal.title"),
                                html: `
                                  <div class="text-left">
                                    <p class="mb-4" style="color: ${isDarkMode ? '#e5e7eb' : '#3a3a3a'};">${t("companies.sgModal.cascadeModal.description")}</p>
                                    <div class="max-h-60 overflow-y-auto rounded-lg p-3 mb-4" style="background-color: ${isDarkMode ? '#3a3a3a' : '#f9fafb'}; border: 1px solid ${isDarkMode ? '#374151' : '#e5e7eb'};">
                                      ${companiesDetails.map(c => `
                                        <div class="mb-3 pb-3 border-b last:border-0 last:mb-0 last:pb-0" style="border-color: ${isDarkMode ? '#42474e' : '#e5e7eb'};">
                                          <div class="font-semibold text-sm" style="color: ${isDarkMode ? '#e5e7eb' : '#111827'};">${escapeHtml(String(c.name ?? ""))}</div>
                                          <div class="text-xs" style="color: ${isDarkMode ? '#9ca3af' : '#4b5563'};">
                                            ${t("companies.sgModal.cascadeModal.sgStatus")}: <span class="font-medium">${c.sgStatus === 'facturable' ? t("companies.sgModal.step1.status.facturable") : c.sgStatus === 'terminado' ? t("companies.sgModal.step1.status.notFacturable") : t("companies.sgModal.step1.status.unknown")}</span>
                                          </div>
                                          <div class="text-xs" style="color: ${isDarkMode ? '#9ca3af' : '#4b5563'};">
                                            Dispositivos: <span class="font-medium">${c.deviceCount}</span>
                                          </div>
                                          <div class="text-xs" style="color: ${isDarkMode ? '#9ca3af' : '#4b5563'};">
                                            Unidades: <span class="font-medium">${c.vehicleCount}</span>
                                          </div>
                                        </div>
                                      `).join('')}
                                    </div>
                                    <p class="text-sm" style="color: ${isDarkMode ? '#9ca3af' : '#4b5563'};">
                                      ${companiesDetails.reduce((acc, c) => acc + c.deviceCount, 0)} ${t("companies.sgModal.cascadeModal.totalDevices")}
                                    </p>
                                  </div>
                                `,
                                icon: "question",
                                showCancelButton: true,
                                confirmButtonColor: brand.primary,
                                cancelButtonColor: "#6b7280",
                                confirmButtonText: t("companies.sgModal.cascadeModal.confirmButton"),
                                cancelButtonText: t("companies.sgModal.buttons.cancel"),
                                width: "500px",
                                background: isDarkMode ? "#3a3a3a" : "#ffffff",
                                color: isDarkMode ? "#e5e7eb" : "#111827",
                              });

                              if (result.isConfirmed) {
                                const totalCompanies = sgCompanies.length;
                                let processedCount = 0;
                                let successCount = 0;
                                let failureCount = 0;
                                const failedCompanies: string[] = [];


                                try {
                                  Swal.close();
                                } catch (e) {
                                  console.error("Error closing modals:", e);
                                }

                                // Show modal and start processing in didOpen callback
                                Swal.fire({
                                  title: "Actualizando compañías...",
                                  html: `
                                    <div class="text-left">
                                      <p class="mb-2">Progreso: <span id="progress-text">0/${totalCompanies}</span></p>
                                      <div class="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                                        <div id="progress-bar" class="bg-blue-600 h-2.5 rounded-full" style="width: 0%"></div>
                                      </div>
                                      <p class="mt-2 text-sm text-gray-500" id="current-company">Preparando actualización...</p>
                                    </div>
                                  `,
                                  allowOutsideClick: false,
                                  showConfirmButton: false,
                                  background: isDarkMode ? "#3a3a3a" : "#ffffff",
                                  color: isDarkMode ? "#e5e7eb" : "#111827",
                                  didOpen: async () => {

                                    try {
                                      const DEVICE_STATUS_ACTIVE = "R5tW0uX4yY9cF6dH2aI8";
                                      const DEVICE_STATUS_INACTIVE = "S6uX1vY5zZ0dG7eI3bJ9";
                                      const COMPANY_STATUS_BILLABLE = "jXmjzHcBD5E1J0wjwHzq";
                                      const COMPANY_STATUS_SUSPENDED = "Y2aD7bE1fF6jM3kO9hP5";


                                      // Update initial status
                                      await new Promise(resolve => setTimeout(resolve, 100));
                                      Swal.update({
                                        html: `
                                          <div class="text-left">
                                            <p class="mb-2">Progreso: <span id="progress-text">0/${totalCompanies}</span></p>
                                            <div class="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                                              <div id="progress-bar" class="bg-blue-600 h-2.5 rounded-full" style="width: 0%"></div>
                                            </div>
                                            <p class="mt-2 text-sm text-gray-500" id="current-company">Iniciando actualización...</p>
                                          </div>
                                        `
                                      });

                                      // Procesar cada compañía secuencialmente con rollback
                                      for (const company of sgCompanies) {
                                        const newStatus = company.sgStatus === 'facturable' ? DEVICE_STATUS_ACTIVE : DEVICE_STATUS_INACTIVE;
                                        const newCompanyStatus = company.sgStatus === 'facturable' ? COMPANY_STATUS_BILLABLE : COMPANY_STATUS_SUSPENDED;


                                        // Actualizar UI con progreso
                                        processedCount++;
                                        const progressPercent = (processedCount / totalCompanies) * 100;
                                        Swal.update({
                                          html: `
                                            <div class="text-left">
                                              <p class="mb-2">Progreso: <span id="progress-text">${processedCount}/${totalCompanies}</span></p>
                                              <div class="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                                                <div id="progress-bar" class="bg-blue-600 h-2.5 rounded-full" style="width: ${progressPercent}%"></div>
                                              </div>
                                              <p class="mt-2 text-sm text-gray-500" id="current-company">Actualizando: ${escapeHtml(String(company.name ?? ""))}</p>
                                            </div>
                                          `
                                        });

                                        try {
                                          // Paso 1: Actualizar Status de Usuarios de esa compañía
                                          await api.post('/users/status-by-company', {
                                            company_id: company.id,
                                            status_id: newStatus
                                          });

                                          // Paso 2: Actualizar Status de Unidades de esa compañía
                                          const vehicles = sgVehiclesData[company.id] || [];
                                          const vehicleUpdatePromises = vehicles.map(vehicle =>
                                            api.put(`/vehicleStatus?id=${vehicle.id}`, { status_id: newStatus })
                                          );
                                          await Promise.all(vehicleUpdatePromises);

                                          // Paso 3: Actualizar Status de Dispositivos de esa Compañía
                                          const devices = sgDevicesData[company.id] || [];
                                          const deviceUpdatePromises = devices.map(device =>
                                            api.put(`/deviceStatus?id=${device.id}`, { status_id: newStatus })
                                          );
                                          await Promise.all(deviceUpdatePromises);

                                          // Paso 4: Actualizar Status de la compañía
                                          await api.put(`/company?id=${company.id}`, { status_id: newCompanyStatus });

                                          successCount++;
                                        } catch (error) {
                                          failureCount++;
                                          failedCompanies.push(company.name);

                                          // Rollback: Revertir los pasos anteriores de esta compañía
                                          try {

                                            // Obtener el status opuesto para revertir
                                            const rollbackStatus = company.sgStatus === 'facturable' ? DEVICE_STATUS_INACTIVE : DEVICE_STATUS_ACTIVE;

                                            // Revertir usuarios
                                            await api.post('/users/status-by-company', {
                                              company_id: company.id,
                                              status_id: rollbackStatus
                                            });

                                            // Revertir vehículos
                                            const vehicles = sgVehiclesData[company.id] || [];
                                            const vehicleRollbackPromises = vehicles.map(vehicle =>
                                              api.put(`/vehicleStatus?id=${vehicle.id}`, { status_id: rollbackStatus })
                                            );
                                            await Promise.all(vehicleRollbackPromises);

                                            // Revertir dispositivos
                                            const devices = sgDevicesData[company.id] || [];
                                            const deviceRollbackPromises = devices.map(device =>
                                              api.put(`/deviceStatus?id=${device.id}`, { status_id: rollbackStatus })
                                            );
                                            await Promise.all(deviceRollbackPromises);

                                          } catch (rollbackError) {
                                            // Error during rollback, log but continue
                                          }
                                        }
                                      }

                                      await Swal.fire({
                                        title: failureCount > 0 ? "Actualización parcial" : "Actualización completada",
                                        text: failureCount > 0
                                          ? `Éxito: ${successCount} compañía(es). Fallo: ${failureCount} compañía(es) (${failedCompanies.join(', ')})`
                                          : `Se actualizaron ${successCount} compañía(es) en cascada exitosamente`,
                                        icon: failureCount > 0 ? "warning" : "success",
                                        confirmButtonText: "Aceptar",
                                        confirmButtonColor: brand.primary,
                                        background: isDarkMode ? "#3a3a3a" : "#ffffff",
                                        color: isDarkMode ? "#e5e7eb" : "#111827",
                                      });
                                      setShowSgModal(false);
                                      setModalStep("companies");
                                      setSelectedSgCompanies(new Set());
                                      setCurrentCompanyIndex(0);
                                      setUpdateType(null);
                                      setSelectedDevices(new Set());
                                    } catch (error) {
                                      console.error("Error en actualización en cascada:", error);
                                      await Swal.fire({
                                        icon: "error",
                                        title: "Error",
                                        text: "No se pudo completar la actualización en cascada",
                                        confirmButtonColor: brand.primary,
                                        background: isDarkMode ? "#3a3a3a" : "#ffffff",
                                        color: isDarkMode ? "#e5e7eb" : "#111827",
                                      });
                                    }
                                  }
                                });
                              } else {
                                // Si cancela, volver a mostrar el modal original
                                setShowSgModal(true);
                              }
                          }}
                          disabled={false}
                          className="bg-blue-500 hover:bg-blue-400 text-gray-900 px-3 sm:px-5 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 sm:gap-2 flex-1 w-full"
                        >
                          <i className="ri-flow-chart"></i>
                          <span>{t("companies.sgModal.buttons.cascade")}</span>
                        </button>
                      </Tooltip>
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setModalStep("companies")}
                      className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText} hover:shadow-md flex-1 sm:flex-none`}
                    >
                      <i className="ri-arrow-left-line mr-1 sm:mr-2"></i>
                      {t("companies.sgModal.buttons.back")}
                    </button>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentCompanyIndex(Math.max(0, currentCompanyIndex - 1))}
                        disabled={currentCompanyIndex === 0}
                        className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText} disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <i className="ri-arrow-left-s-line"></i>
                      </button>
                      <button
                        onClick={() => setCurrentCompanyIndex(Math.min(selectedCompaniesList.length - 1, currentCompanyIndex + 1))}
                        disabled={currentCompanyIndex === selectedCompaniesList.length - 1}
                        className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText} disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isDeviceSelectionMode ? (
                        <button
                          onClick={() => setIsDeviceSelectionMode(true)}
                          className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 border ${themeClasses.cardBorder} ${themeClasses.buttonText} hover:shadow-md`}
                        >
                          <i className="ri-checkbox-multiple-line mr-1 sm:mr-2"></i>
                          Seleccionar
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={async () => {
                              if (selectedDevices.size === 0) {
                                Swal.fire({
                                  icon: "warning",
                                  title: "Advertencia",
                                  text: "Por favor selecciona al menos un dispositivo",
                                  confirmButtonColor: brand.primary,
                                });
                                return;
                              }

                              const result = await Swal.fire({
                                title: "Activar dispositivos",
                                text: `¿Estás seguro de que quieres activar ${selectedDevices.size} dispositivo(s)?`,
                                icon: "question",
                                showCancelButton: true,
                                confirmButtonColor: "#10b981",
                                cancelButtonColor: "#6b7280",
                                confirmButtonText: "Activar",
                                cancelButtonText: "Cancelar",
                              });

                              if (result.isConfirmed) {
                                const newChanges = { ...deviceStatusChanges };
                                selectedDevices.forEach(deviceId => {
                                  newChanges[deviceId] = "activo";
                                });
                                setDeviceStatusChanges(newChanges);
                                setSelectedDevices(new Set());
                                setIsDeviceSelectionMode(false);

                                Swal.fire({
                                  icon: "success",
                                  title: "Activados",
                                  text: `${selectedDevices.size} dispositivo(s) han sido marcados para activación`,
                                  confirmButtonColor: brand.primary,
                                });
                              }
                            }}
                            disabled={selectedDevices.size === 0}
                            className="bg-emerald-500 hover:bg-emerald-400 text-white px-3 sm:px-5 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 sm:gap-2"
                          >
                            <i className="ri-check-line"></i>
                            <span>Activar</span>
                          </button>
                          <button
                            onClick={async () => {
                              if (selectedDevices.size === 0) {
                                Swal.fire({
                                  icon: "warning",
                                  title: "Advertencia",
                                  text: "Por favor selecciona al menos un dispositivo",
                                  confirmButtonColor: brand.primary,
                                });
                                return;
                              }

                              const result = await Swal.fire({
                                title: "Desactivar dispositivos",
                                text: `¿Estás seguro de que quieres desactivar ${selectedDevices.size} dispositivo(s)?`,
                                icon: "question",
                                showCancelButton: true,
                                confirmButtonColor: "#ef4444",
                                cancelButtonColor: "#6b7280",
                                confirmButtonText: "Desactivar",
                                cancelButtonText: "Cancelar",
                              });

                              if (result.isConfirmed) {
                                const newChanges = { ...deviceStatusChanges };
                                selectedDevices.forEach(deviceId => {
                                  newChanges[deviceId] = "inactivo";
                                });
                                setDeviceStatusChanges(newChanges);
                                setSelectedDevices(new Set());
                                setIsDeviceSelectionMode(false);

                                Swal.fire({
                                  icon: "success",
                                  title: "Desactivados",
                                  text: `${selectedDevices.size} dispositivo(s) han sido marcados para desactivación`,
                                  confirmButtonColor: brand.primary,
                                });
                              }
                            }}
                            disabled={selectedDevices.size === 0}
                            className="bg-rose-500 hover:bg-rose-400 text-white px-3 sm:px-5 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 sm:gap-2"
                          >
                            <i className="ri-close-line"></i>
                            <span>Desactivar</span>
                          </button>
                        </>
                      )}
                    </div>
                    <button
                      onClick={async () => {
                        if (isDeviceSelectionMode) {
                          Swal.fire({
                            icon: "warning",
                            title: "Modo de selección activo",
                            text: "Por favor completa la selección de dispositivos antes de actualizar",
                            confirmButtonColor: brand.primary,
                          });
                          return;
                        }
                        if (updateType === "cascade") {
                          // Actualización en cascada: compañía -> usuarios -> dispositivos -> vehículos con status de SG
                          const result = await Swal.fire({
                            title: t("companies.sgModal.manualConfirm.title"),
                            text: t("companies.sgModal.manualConfirm.text", { count: selectedCompaniesList.length }),
                            icon: "question",
                            showCancelButton: true,
                            confirmButtonColor: brand.primary,
                            cancelButtonColor: "#6b7280",
                            confirmButtonText: t("companies.sgModal.manualConfirm.confirmButton"),
                            cancelButtonText: t("companies.sgModal.buttons.cancel"),
                          });

                          if (result.isConfirmed) {
                            await Swal.fire({
                              title: "Actualizando...",
                              text: "Por favor espera",
                              allowOutsideClick: false,
                              didOpen: () => {
                                Swal.showLoading();
                              },
                            });

                            try {
                              const DEVICE_STATUS_ACTIVE = "R5tW0uX4yY9cF6dH2aI8";
                              const DEVICE_STATUS_INACTIVE = "S6uX1vY5zZ0dG7eI3bJ9";
                              const COMPANY_STATUS_BILLABLE = "jXmjzHcBD5E1J0wjwHzq";
                              const COMPANY_STATUS_SUSPENDED = "Y2aD7bE1fF6jM3kO9hP5";

                              // Paso 1: Actualizar status de la compañía al de SG
                              const companyUpdatePromises = selectedCompaniesList.map(async (company) => {
                                const newCompanyStatus = company.sgStatus === 'facturable' ? COMPANY_STATUS_BILLABLE : COMPANY_STATUS_SUSPENDED;
                                return api.put(`/company?id=${company.id}`, { status_id: newCompanyStatus });
                              });
                              await Promise.all(companyUpdatePromises);

                              // Paso 2: Actualizar status de los usuarios al de SG
                              for (const company of selectedCompaniesList) {
                                const userStatus = company.sgStatus === 'facturable' ? DEVICE_STATUS_ACTIVE : DEVICE_STATUS_INACTIVE;
                                await api.post('/users/status-by-company', {
                                  company_id: company.id,
                                  status_id: userStatus
                                });
                              }

                              // Paso 3: Actualizar todos los dispositivos al status de SG
                              const deviceUpdatePromises = [];
                              const deviceVehicleIds = [];
                              let devicesUpdatedCount = 0;

                              for (const company of selectedCompaniesList) {
                                const devices = sgDevicesData[company.id] || [];
                                for (const device of devices) {
                                  const newStatus = company.sgStatus === 'facturable' ? DEVICE_STATUS_ACTIVE : DEVICE_STATUS_INACTIVE;
                                  deviceUpdatePromises.push(
                                    api.put(`/deviceStatus?id=${device.id}`, { status_id: newStatus })
                                  );
                                  if (device.vehicle_id) {
                                    deviceVehicleIds.push({ vehicleId: device.vehicle_id, status: newStatus });
                                  }
                                  devicesUpdatedCount++;
                                }
                              }

                              await Promise.all(deviceUpdatePromises);

                              // Paso 4: Actualizar todos los vehículos al status de SG
                              const vehicleUpdatePromises = deviceVehicleIds.map(({ vehicleId, status }) =>
                                api.put(`/vehicleStatus?id=${vehicleId}`, { status_id: status })
                              );
                              await Promise.all(vehicleUpdatePromises);

                              await Swal.fire({
                                title: "Actualización completada",
                                text: `Se actualizaron ${selectedCompaniesList.length} compañía(es), sus usuarios, ${devicesUpdatedCount} dispositivo(s) y ${deviceVehicleIds.length} vehículo(es) en cascada`,
                                icon: "success",
                                confirmButtonText: "Aceptar",
                                confirmButtonColor: brand.primary,
                              });
                              setShowSgModal(false);
                              setModalStep("companies");
                              setSelectedSgCompanies(new Set());
                              setCurrentCompanyIndex(0);
                              setUpdateType(null);
                              setSelectedDevices(new Set());
                              setDeviceStatusChanges({});
                              setSgDevicesData({});
                            } catch (error) {
                              console.error("Error updating cascade:", error);
                              Swal.fire({
                                title: "Error",
                                text: "Ocurrió un error al actualizar. Por favor intenta nuevamente.",
                                icon: "error",
                                confirmButtonText: "Aceptar",
                                confirmButtonColor: brand.primary,
                              });
                            }
                          }
                        } else if (updateType === "manual") {
                          // Actualización manual: compañía -> usuarios -> dispositivos (con status del dropdown) -> vehículos
                          const result = await Swal.fire({
                            title: t("companies.sgModal.manualConfirm.title"),
                            text: t("companies.sgModal.manualConfirm.text", { count: selectedCompaniesList.length }),
                            icon: "question",
                            showCancelButton: true,
                            confirmButtonColor: brand.primary,
                            cancelButtonColor: "#6b7280",
                            confirmButtonText: t("companies.sgModal.manualConfirm.confirmButton"),
                            cancelButtonText: t("companies.sgModal.buttons.cancel"),
                          });

                          if (result.isConfirmed) {
                            await Swal.fire({
                              title: "Actualizando...",
                              text: "Por favor espera",
                              allowOutsideClick: false,
                              didOpen: () => {
                                Swal.showLoading();
                              },
                            });

                            try {
                              const DEVICE_STATUS_ACTIVE = "R5tW0uX4yY9cF6dH2aI8";
                              const DEVICE_STATUS_INACTIVE = "S6uX1vY5zZ0dG7eI3bJ9";
                              const COMPANY_STATUS_BILLABLE = "jXmjzHcBD5E1J0wjwHzq";
                              const COMPANY_STATUS_SUSPENDED = "Y2aD7bE1fF6jM3kO9hP5";

                              // Paso 1: Actualizar status de la compañía al de SG
                              const companyUpdatePromises = selectedCompaniesList.map(async (company) => {
                                const newCompanyStatus = company.sgStatus === 'facturable' ? COMPANY_STATUS_BILLABLE : COMPANY_STATUS_SUSPENDED;
                                return api.put(`/company?id=${company.id}`, { status_id: newCompanyStatus });
                              });
                              await Promise.all(companyUpdatePromises);

                              // Paso 2: Actualizar status de los usuarios correspondientes a la empresa con el status de SG
                              for (const company of selectedCompaniesList) {
                                const userStatus = company.sgStatus === 'facturable' ? DEVICE_STATUS_ACTIVE : DEVICE_STATUS_INACTIVE;
                                await api.post('/users/status-by-company', {
                                  company_id: company.id,
                                  status_id: userStatus
                                });
                              }

                              // Paso 3: Actualizar dispositivos con el status seleccionado en el dropdown
                              const deviceUpdatePromises = [];
                              const deviceVehicleIds = [];
                              let devicesUpdatedCount = 0;

                              for (const companyId of selectedCompaniesList.map(c => c.id)) {
                                const devices = sgDevicesData[companyId] || [];
                                for (const device of devices) {
                                  if (deviceStatusChanges[device.id]) {
                                    const newStatus = deviceStatusChanges[device.id] === 'activo' ? DEVICE_STATUS_ACTIVE : DEVICE_STATUS_INACTIVE;
                                    deviceUpdatePromises.push(
                                      api.put(`/deviceStatus?id=${device.id}`, { status_id: newStatus })
                                    );
                                    if (device.vehicle_id) {
                                      deviceVehicleIds.push({ vehicleId: device.vehicle_id, status: newStatus });
                                    }
                                    devicesUpdatedCount++;
                                  }
                                }
                              }

                              await Promise.all(deviceUpdatePromises);

                              // Paso 4: Actualizar vehículos de los dispositivos actualizados
                              const vehicleUpdatePromises = deviceVehicleIds.map(({ vehicleId, status }) =>
                                api.put(`/vehicleStatus?id=${vehicleId}`, { status_id: status })
                              );
                              await Promise.all(vehicleUpdatePromises);

                              await Swal.fire({
                                title: "Actualización completada",
                                text: `Se actualizaron ${selectedCompaniesList.length} compañía(es), sus usuarios, ${devicesUpdatedCount} dispositivo(s) y ${deviceVehicleIds.length} vehículo(es)`,
                                icon: "success",
                                confirmButtonText: "Aceptar",
                                confirmButtonColor: brand.primary,
                              });
                              setShowSgModal(false);
                              setModalStep("companies");
                              setSelectedSgCompanies(new Set());
                              setCurrentCompanyIndex(0);
                              setUpdateType(null);
                              setSelectedDevices(new Set());
                              setDeviceStatusChanges({});
                              setSgDevicesData({});
                            } catch (error) {
                              console.error("Error updating:", error);
                              Swal.fire({
                                title: "Error",
                                text: "Ocurrió un error al actualizar. Por favor intenta nuevamente.",
                                icon: "error",
                                confirmButtonText: "Aceptar",
                                confirmButtonColor: brand.primary,
                              });
                            }
                          }
                        } 
                      }}
                      className="bg-primary-500 hover:bg-primary-400 text-gray-900 px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2"
                    >
                      <i className="ri-refresh-line"></i>
                      {t("companies.sgModal.buttons.update")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
